import { PlanCardType, ResturantData } from "@/types/globalTypes";
import { AppConfig } from "@/utils/AppConfig";
import { formatMoney, getDaysLeft } from "@/utils/helpers";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

type PlanCardProps = {
  card: PlanCardType;
  handleRequestSubscription: (plan: string, ModalTitle: string) => void;
  isRequesting: boolean;
};

function PlanCard({
  card,
  handleRequestSubscription,
  isRequesting,
}: PlanCardProps) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const title = lang === "ar" ? card.titleAR : card.titleEN;
  const description = lang === "ar" ? card.descriptionAR : card.descriptionEN;

  const ResturantData = useSelector(
    (state: { resturant: { resturantData: ResturantData } }) =>
      state.resturant.resturantData,
  );

  const WithoutPlan = ResturantData?.subscription === null
  const isPaidPlan = ResturantData?.subscription?.isFree === false
  const freePlanCard = card?.isFreeTrial
  const shouldShowFreePlanCard = (freePlanCard && isPaidPlan) || (WithoutPlan && freePlanCard)

  const SubscribedPlanId = ResturantData?.subscription?.plan // planId
  const cardPlanId = card._id

  const isActivePlan = SubscribedPlanId === cardPlanId

  const isRemainsOneMonthOrBelow = ResturantData?.subscription?.expire 
  ? getDaysLeft(ResturantData?.subscription?.expire) <= 30 
  : true

  const isPaidPlanAndNotRemainsOneMonth = isPaidPlan && !isRemainsOneMonthOrBelow

  if (shouldShowFreePlanCard) return null
  

  return (
    <div className="relative flex flex-col rounded-lg border border-[#BAF3FD] bg-white px-5 py-5 shadow-lg transition-shadow duration-200 hover:shadow-xl">
      <div className="flex items-center justify-between">
        <h4 className="text-xl font-semibold text-[#1B2B34]">{title}</h4>
        <div className="flex gap-2 items-center justify-end flex-wrap">
          {
            isActivePlan && <span className="h-[25px] w-[65px] rounded-full bg-sidebar text-center font-semibold capitalize text-skyblue">
            {t("subscribtionSettings.active")}
          </span>
        }
        <span className="h-[25px] w-[65px] rounded-full bg-[#BAF3FD] text-center font-semibold capitalize text-[#1B2B34]">
          {card.isFreeTrial
            ? t("subscribtionSettings.trial")
            : t("subscribtionSettings.pro")}
        </span>
            </div>
      </div>

      

      {/* Price */}
      <div className="mt-3 flex items-center justify-between pe-1 font-semibold">
        <p>{t("subscribtionSettings.price")}</p>
        <p dir="rtl">
          {!Number(card.price)
            ? t("subscribtionSettings.free")
            : formatMoney(card.price) + " "}
          {!Number(card.price) ? null : AppConfig.currency.icon()}
        </p>
      </div>

      {/* Period */}
      <div className="mt-1 flex items-center justify-between pe-1 font-semibold">
        <p>{t("subscribtionSettings.subscriptionPeriod")}</p>
        <p>
          {card.period}{" "}
          {Number(card.period) <= 1
            ? t("subscribtionSettings.month")
            : t("subscribtionSettings.months")}
        </p>
      </div>

      <hr className="my-3" />

      <p className="mb-3 text-base text-black">{description}</p>

      <div className="flex-grow" />

      {card.isFreeTrial ? null : (
        <button
          onClick={() => handleRequestSubscription(card._id, title)}
          disabled={isRequesting || isPaidPlanAndNotRemainsOneMonth}
          className={`mt-auto block w-full rounded-full bg-[#BAF3FD] px-4 py-2 font-semibold capitalize text-[#1B2B34] transition-colors hover:bg-[#1B2B34] hover:text-skyblue disabled:cursor-not-allowed disabled:bg-gray-400 disabled:text-gray-600`}
        >
          {isPaidPlanAndNotRemainsOneMonth
            ? t("subscribtionSettings.alreadySubscribed")
            : t("subscribtionSettings.subscribe")}
        </button>
      )}
    </div>
  );
}

export default PlanCard;
